import { useEffect, useMemo } from 'react';
import { skipToken, useQuery } from '@apollo/client/react';

import {
    IUserListItemFragment,
    ITrainerSelectorQueryVariables,
    TrainerSelectorDocument,
    IUserSelectorInput,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';

export interface IUserList {
    trainers: IUserListItemFragment[];
    alreadyAddedTrainers: string[];
    trainersCount?: number | null;
    loading: boolean;
}

export const useTrainerList = (
    trainerQueryVariables?: ITrainerSelectorQueryVariables,
    skip?: boolean,
    selectorQueryVariables?: IUserSelectorInput
): IUserList => {
    const { showApolloError } = useApolloError();
    const shouldFetch = !!trainerQueryVariables && !skip;

    const { loading, data, error } = useQuery(
        TrainerSelectorDocument,
        shouldFetch
            ? {
                  variables: {
                      q: trainerQueryVariables.q,
                      offset: trainerQueryVariables.offset,
                      ...trainerQueryVariables,
                      selector: selectorQueryVariables,
                  },
              }
            : skipToken
    );

    useEffect(() => {
        if (error) {
            showApolloError(error);
        }
    }, [error, showApolloError]);

    const trainersObject = data?.trainers;
    const trainersCount = trainersObject?.count;
    const trainers = useMemo(
        () =>
            (trainersObject?.edges
                .map((edge) => edge?.node || null)
                .filter(Boolean) as IUserListItemFragment[]) || [],
        [trainersObject?.edges]
    );

    const alreadyAddedTrainers = useMemo(
        () =>
            trainersObject?.edges
                .map((edge) => {
                    if (edge?.alreadyAddedToObject) return edge.node.id;

                    return null;
                })
                .filter(Boolean) || [],
        [trainersObject?.edges]
    );

    return {
        trainers,
        alreadyAddedTrainers,
        trainersCount,
        loading,
    };
};
